<template>
  <div class="miniapps-page full-container">
    <div class="search-bar" style="padding: 15px; border-bottom: 1px solid var(--border);">
      <div class="flex-wrap">
        <Input
          v-model="searchText"
          placeholder="搜索应用名称"
          clearable
          style="width: 280px; margin-right: 10px;"
          @on-enter="handleSearch"
        />
        <Button type="primary" icon="md-search" @click="handleSearch" style="margin-right: 10px;">搜索</Button>
        <Button type="success" icon="md-add" @click="handleAdd">新建</Button>
      </div>
    </div>

    <div class="table-wrap">
      <BaseTable
        ref="table"
        full
        :columns="columns"
        :data="filteredData"
        :loading="loading"
        border
        @event="handleTableEvent"
      />
    </div>

    <!-- 新建/修改（基本信息）对话框 -->
    <Modal
      v-model="metaModalVisible"
      :title="metaModalTitle"
      width="560"
      @on-ok="handleMetaSubmit"
      :loading="metaConfirmLoading"
      @on-cancel="metaModalVisible = false"
    >
      <Form ref="metaFormRef" :model="metaFormData" :rules="metaRules" :label-width="100">
        <FormItem label="应用名称" prop="name">
          <Input v-model="metaFormData.name" placeholder="请输入应用名称" />
        </FormItem>
        <FormItem label="描述" prop="description">
          <Input v-model="metaFormData.description" type="textarea" :rows="3" placeholder="请输入描述" />
        </FormItem>
        <FormItem label="图标" prop="icon">
          <div class="icon-clip-wrap">
            <ImageClip
              v-model="metaFormData.icon"
              :editable="true"
              :option="iconClipOption"
            />
            <span class="icon-hint">裁剪为 200×200 使用</span>
          </div>
        </FormItem>
        <FormItem label="需登录" prop="private">
          <i-switch v-model="metaFormData.private" :true-value="false" :false-value="true">
            <span slot="open">是</span>
            <span slot="close">否</span>
          </i-switch>
          <span class="form-hint">否 = 仅自己可见，是 = 所有人可见</span>
        </FormItem>
        <FormItem label="状态" prop="disabled" v-if="metaEditId">
          <i-switch v-model="metaFormData.disabled" :true-value="1" :false-value="0">
            <span slot="open">禁用</span>
            <span slot="close">启用</span>
          </i-switch>
        </FormItem>
      </Form>
    </Modal>

    <!-- 应用编辑对话框：左侧预览/代码，右侧上 AI 对话、下组件占位 -->
    <Modal
      v-model="editorModalVisible"
      :title="editorTitle"
      width="1400"
      class-name="miniapp-editor-modal"
      :footer-hide="true"
      @on-cancel="closeEditor"
    >
      <div class="miniapp-editor-layout">
        <div class="editor-left">
          <div class="editor-left-toolbar">
            <RadioGroup v-model="viewMode" type="button" size="small">
              <Radio label="preview">预览</Radio>
              <Radio label="code">代码</Radio>
            </RadioGroup>
            <Button type="primary" size="small" icon="md-checkmark" :loading="contentSaving" @click="saveContent">保存</Button>
          </div>
          <div class="preview-size-bar" v-if="viewMode === 'preview'">
            <span class="preview-size-label">尺寸：</span>
            <Button
              v-for="(sz, idx) in previewSizes"
              :key="idx"
              :type="previewSizeIndex === idx ? 'primary' : 'default'"
              size="small"
              @click="previewSizeIndex = idx"
            >{{ sz.label || `${sz.w}×${sz.h}` }}</Button>
          </div>
          <div class="editor-left-body">
            <template v-if="viewMode === 'preview'">
              <div class="preview-viewport" :style="previewViewportStyle">
                <div class="preview-iframe-wrap" v-if="previewUrl">
                  <iframe
                    :src="previewUrl"
                    class="preview-iframe"
                    :style="previewIframeStyle"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                    title="预览"
                  />
                </div>
                <div v-else class="preview-empty">暂无内容可预览</div>
              </div>
            </template>
            <Input
              v-show="viewMode === 'code'"
              v-model="editorContent"
              type="textarea"
              :rows="20"
              :maxlength="codeTextareaMaxLength"
              placeholder="在此编辑 HTML 内容"
              class="code-textarea"
            />
          </div>
        </div>
        <div class="editor-right">
          <div class="editor-right-top chat-panel">
            <div class="chat-panel-header">
              <span class="chat-panel-title">AI 对话</span>
            </div>
            <div class="chat-messages-wrap" ref="chatMessagesWrap">
              <div
                v-for="(msg, idx) in chatMessages"
                :key="idx"
                :class="['dev-chat-msg', msg.role === 'user' ? 'msg-user' : 'msg-bot']"
              >
                <div class="msg-bubble" :class="msg.role">
                  <template v-if="msg.role === 'user'">{{ msg.content }}</template>
                  <template v-else>
                    <div class="msg-markdown-wrap" :class="{ 'msg-streaming': msg.streaming }">
                      <div class="msg-answer markdown-body" v-if="msg.content">
                        <BaseMarkdownView :value="msg.content" />
                      </div>
                      <span v-else-if="msg.streaming" class="cursor-blink">...</span>
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="chatSending && !streamingMessage" class="dev-chat-msg msg-bot">
                <div class="msg-bubble bot typing">正在回复...</div>
              </div>
            </div>
            <div class="chat-input-row">
              <Input
                v-model="chatInput"
                type="textarea"
                :rows="2"
                placeholder="描述你想要的页面或修改，按 Enter 发送"
                @on-keydown="onChatKeydown"
                class="chat-input-area"
              />
              <Button type="primary" size="small" :loading="chatSending" @click="sendChatMessage" class="chat-send-btn">发送</Button>
            </div>
          </div>
          <div class="editor-right-bottom">
            <div class="panel-title">技能</div>
            <div v-if="skillsList.length === 0 && !skillsLoading" class="panel-placeholder">暂无技能</div>
            <Spin v-else-if="skillsLoading" fix />
            <div v-else class="skills-list">
              <div v-for="(sk, idx) in skillsList" :key="sk.id || idx" class="skill-item">
                <div class="skill-info">
                  <span class="skill-name">{{ sk.name }}</span>
                  <span v-if="sk.description" class="skill-desc">{{ sk.description }}</span>
                </div>
                <i-switch v-model="sk.enabled" size="small" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import ImageClip from '@/components/form/ImageClip';
import BaseMarkdownView from '@/components/base/MarkdownView';

const API_BASE = '/ai/miniapps';
const SKILLS_API = '/ai/skills';
const CHAT_API = '/ai/chat';

const BASE_SYSTEM_PROMPT = `你是一个 HTML 页面开发助手。请根据用户需求开发一个完整的、可单独运行的 HTML 页面。
要求：
1. 引入的库必须使用国内可访问的 CDN（如 bootcdn.cn、staticfile.org、字节等），不要使用国内无法访问的库。
2. 界面简洁、清晰。
3. 直接输出完整可运行的 HTML 代码，不要省略关键部分。代码请放在 \`\`\`html 代码块中。`;

export default {
  components: { ImageClip, BaseMarkdownView },
  data() {
    return {
      loading: false,
      items: [],
      searchText: '',

      metaModalVisible: false,
      metaModalTitle: '新建应用',
      metaConfirmLoading: false,
      metaEditId: null,
      metaFormData: {
        name: '',
        icon: '',
        content: '',
        description: '',
        private: true,
        disabled: 0,
      },
      metaRules: {
        name: [{ required: true, message: '请输入应用名称', trigger: 'blur' }],
      },
      iconClipOption: {
        uploader: 'session/upload',
        w2h: 1,
      },

      editorModalVisible: false,
      editorEditId: null,
      editorTitle: '编辑应用',
      viewMode: 'preview',
      editorContent: '',
      /** 代码区 textarea 最大字符数，避免组件默认限制导致粘贴不完整 */
      codeTextareaMaxLength: 2000000,
      contentSaving: false,
      previewSizes: [
        { w: 1920, h: 1280 },
        { w: 1024, h: 768 },
        { w: 900, h: 600 },
        { w: 375, h: 700, label: '375×700(移动端)' },
      ],
      previewSizeIndex: 0,
      /** 统一显示比例，不同分辨率只体现宽高比，不做过大缩放 */
      previewDisplayScale: 0.45,

      chatMessages: [],
      chatInput: '',
      chatSending: false,
      streamingMessage: false,
      skillsList: [],
      skillsLoading: false,

      /** 预览用临时 Blob URL */
      previewBlobUrl: null,

      columns: [
        { type: 'index', width: 60, title: '序号' },
        {
          title: '图标',
          key: 'icon',
          width: 80,
          align: 'center',
          render: (h, params) => {
            const icon = params.row.icon;
            if (!icon) return h('span', '-');
            return h('img', {
              attrs: { src: icon, alt: '' },
              style: { width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' },
            });
          },
        },
        {
          title: '应用名称',
          key: 'name',
          minWidth: 160,
          type: 'text',
          linkEvent: 'open',
        },
        {
          title: '需登录',
          key: 'private',
          width: 80,
          align: 'center',
          render: (h, params) => {
            const v = params.row.private;
            return h('span', v === false ? '是' : '否');
          },
        },
        {
          title: '启用',
          key: 'disabled',
          width: 100,
          align: 'center',
          render: (h, params) => {
            const row = params.row;
            const isDisabled = row.disabled === 1;
            const canEdit = this.canEdit(row);
            if (!canEdit) {
              return h('span', { class: 'status-text' }, isDisabled ? '禁用' : '启用');
            }
            return h('i-switch', {
              props: {
                value: isDisabled ? 1 : 0,
                'true-value': 0,
                'false-value': 1,
                size:'small'
              },
              on: {
                'on-change': (v) => this.toggleDisabled(row, v),
              },
            });
          },
        },
        {
          title:"查看次数",
          type:"number",
          width:100,
          key:'count'
        },
        {
          title: '创建时间',
          key: 'created_at',
          width: 160,
          type:'time'
         
        },
        {
          title:'创建人',
          type:'user',
          key:'created_by',
          width:120
        },
        {
          title: '编辑',
          key: 'edit',
          width: 80,
          align: 'center',
          render: (h, params) => {
            if (!this.canEdit(params.row)) return h('span', '-');
            return h('Button', {
              props: { size: 'small' },
              on: { click: () => this.handleTableEvent({ type: 'edit', data: params.row }) },
            }, '编辑');
          },
        },
        {
          title: '操作',
          key: 'action',
          fixed: 'right',
          width: 140,
          render: (h, params) => {
            const row = params.row;
            if (!this.canEdit(row)) return h('span', { style: { color: 'var(--subtext2)' } }, '-');
            return h('div', [
              h('Button', {
                props: { size: 'small' },
                style: { marginRight: '8px' },
                on: { click: () => this.handleTableEvent({ type: 'modify', data: row }) },
              }, '修改'),
              h('Button', {
                props: { size: 'small' },
                on: { click: () => this.handleDelete(row) },
              }, '删除'),
            ]);
          },
        },
      ],
    };
  },
  computed: {
    session() {
      return this.$store.getters['session/session'] || {};
    },
    isAdmin() {
      return this.session.roles && this.session.roles.some(r => r.id === 'admin');
    },
    filteredData() {
      let result = [...this.items];
      if (this.searchText) {
        const q = this.searchText.toLowerCase();
        result = result.filter(item => (item.name && item.name.toLowerCase().includes(q)));
      }
      return result;
    },
    currentPreviewSize() {
      const list = this.previewSizes || [];
      const idx = this.previewSizeIndex;
      return list[idx] || list[0] || { w: 1920, h: 1280 };
    },
    /** 视口容器尺寸 = 分辨率 × 统一比例，只体现比例、不出现内层滚动条 */
    previewViewportStyle() {
      const sz = this.currentPreviewSize;
      const scale = this.previewDisplayScale || 0.45;
      const w = Math.round((sz.w || 1920) * scale);
      const h = Math.round((sz.h || 1280) * scale);
      return {
        width: w + 'px',
        height: h + 'px',
      };
    },
    previewInnerStyle() {
      const sz = this.currentPreviewSize;
      const scale = this.previewDisplayScale || 0.45;
      return {
        width: (sz.w || 1920) + 'px',
        height: (sz.h || 1280) + 'px',
        zoom: scale,
      };
    },
    /** 预览地址：Blob URL（无后端预览接口） */
    previewUrl() {
      return this.previewBlobUrl || '';
    },
    /** iframe 尺寸与缩放，与视口比例一致 */
    previewIframeStyle() {
      const sz = this.currentPreviewSize;
      const scale = this.previewDisplayScale || 0.45;
      return {
        width: (sz.w || 1920) + 'px',
        height: (sz.h || 1280) + 'px',
        transform: `scale(${scale})`,
        transformOrigin: '0 0',
      };
    },
    systemPrompt() {
      let prompt = BASE_SYSTEM_PROMPT;
      const enabled = (this.skillsList || []).filter(s => s.enabled && (s.content || '').trim());
      if (enabled.length) {
        prompt += '\n\n以下技能说明请一并遵守：\n';
        enabled.forEach(s => { prompt += (s.content || '').trim() + '\n\n'; });
      }
      return prompt.trim();
    },
    /** 内部应用：当前勾选的技能中包含 ID=3 时，预览 URL 增加 authkey */
    editorIsInternal() {
      return (this.skillsList || []).some(s => s.enabled && String(s.id) === '3');
    },
  },
  watch: {
    editorContent() {
      if (this.editorModalVisible && this.viewMode === 'preview') {
        if (this._previewUpdateTimer) clearTimeout(this._previewUpdateTimer);
        this._previewUpdateTimer = setTimeout(() => this.updatePreviewUrl(), 300);
      }
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.fetchData();
    });
  },
  methods: {
    canEdit(row) {
      if (!this.session || !this.session.id) return false;
      return this.isAdmin || row.created_by === this.session.id;
    },
    formatDate(v) {
      if (!v) return '-';
      if (typeof v === 'number' && v > 1e12) return new Date(v).toLocaleString();
      return String(v);
    },
    handleSearch() {},
    async fetchData() {
      this.loading = true;
      try {
        const res = await this.$api.get(API_BASE);
        this.items = (res.data && res.data.data) || res.data || [];
        if (!Array.isArray(this.items)) this.items = [];
      } catch (err) {
        this.$Message.error('获取应用列表失败');
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    handleAdd() {
      this.metaModalTitle = '新建应用';
      this.metaEditId = null;
      this.metaFormData = {
        name: '',
        icon: '',
        content: '',
        description: '',
        private: true,
        disabled: 0,
      };
      this.metaModalVisible = true;
      this.$nextTick(() => this.$refs.metaFormRef && this.$refs.metaFormRef.resetFields());
    },
    handleTableEvent(e) {
      const { type, data: row } = e || {};
      if (type === 'open') {
        this.openAppInNewWindow(row);
      } else if (type === 'edit') {
        this.openEditor(row);
      } else if (type === 'modify') {
        this.openMetaEdit(row);
      } else if (type === 'delete') {
        this.handleDelete(row);
      }
    },
    getAppViewUrl(item) {
      const base = (this.$api.defaults.baseURL || '').replace(/\/$/, '');
      let id = item.id
      if(item.private){
        return `${base}/public/miniapps/${encodeURIComponent(id)}?authkey=${this.session.token.slice(7)}`;
      }
      else{
        return `${base}/public/miniapps/${encodeURIComponent(id)}`;
      }
    },
    openAppInNewWindow(row) {
      if (!row || !row.id) return;
      window.open(this.getAppViewUrl(row), '_blank');
    },
    openEditor(row) {
      this.editorEditId = row.id;
      this.editorTitle = `编辑：${row.name || '应用'}`;
      this.editorContent = row.content || '';
      this.viewMode = 'preview';
      this.chatMessages = [];
      this.chatInput = '';
      this.editorModalVisible = true;
      this.fetchSkills(row && row.skills);
      this.$nextTick(() => this.updatePreviewUrl());
    },
    closeEditor() {
      this.revokePreviewUrl();
      this.editorModalVisible = false;
      this.editorEditId = null;
      this.editorContent = '';
      this.chatMessages = [];
      this.skillsList = [];
    },
    /** 撤销预览 Blob URL，避免内存泄漏 */
    revokePreviewUrl() {
      if (this.previewBlobUrl) {
        URL.revokeObjectURL(this.previewBlobUrl);
        this.previewBlobUrl = null;
      }
    },
    /** 根据当前 editorContent 生成预览 Blob URL */
    updatePreviewUrl() {
      this.revokePreviewUrl();
      const raw = (this.editorContent || '').trim();
      if (!raw) return;
      const blob = new Blob([raw], { type: 'text/html; charset=utf-8' });
      this.previewBlobUrl = URL.createObjectURL(blob);
    },
    /** 获取当前勾选技能的 ID 列表，逗号拼接（用于更新 miniapp.skills） */
    getEditorSkillsIds() {
      return (this.skillsList || []).filter(s => s.enabled).map(s => s.id).join(',');
    },
    async fetchSkills(appSkills) {
      const skillIds = (appSkills != null && appSkills !== '' ? String(appSkills) : '').split(',').map(s => String(s).trim()).filter(Boolean);
      this.skillsLoading = true;
      try {
        const res = await this.$api.get(SKILLS_API);
        const raw = (res.data && res.data.data) || res.data || [];
        const list = Array.isArray(raw) ? raw : [];
        this.skillsList = list.map(s => ({
          ...s,
          id: s.id,
          name: s.name || '',
          description: s.description || '',
          content: s.content || '',
          enabled: skillIds.includes(String(s.id)),
        }));
      } catch (err) {
        this.$Message.error('获取技能列表失败');
        this.skillsList = [];
      } finally {
        this.skillsLoading = false;
      }
    },
    onChatKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendChatMessage();
      }
    },
    sendChatMessage() {
      const text = (this.chatInput || '').trim();
      if (!text || this.chatSending) return;
      this.chatMessages.push({ role: 'user', content: text });
      this.chatInput = '';
      this.chatSending = true;
      this.streamingMessage = false;
      this.$nextTick(() => this.scrollChatToBottom());

      const streamDone = (errMsg) => {
        const last = this.chatMessages[this.chatMessages.length - 1];
        if (last && last.streaming) {
          last.streaming = false;
          if (errMsg) last.content = errMsg;
        }
        this.streamingMessage = false;
        this.chatSending = false;
        this.$nextTick(() => {
          this.scrollChatToBottom();
          if (!errMsg && last && last.content) this.extractCodeToContent(last.content);
        });
      };

      this.devStreamChat(text, streamDone).catch(e => {
        const errMsg = (e && e.message) || '请求失败';
        this.chatMessages.push({ role: 'assistant', content: errMsg });
        streamDone();
      });
    },
    async devStreamChat(userText, streamDone) {
      const messages = [
        { role: 'system', content: this.systemPrompt },
        ...this.chatMessages.filter(m => m.role !== 'system').map(m => ({ role: m.role, content: m.content })),
      ];
      const url = (this.$api.defaults.baseURL || '').replace(/\/$/, '') + CHAT_API;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...(this.$api.defaults.headers || {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messages }),
        credentials: 'same-origin',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.msg || `HTTP ${res.status}`);
      }

      const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
      const isStream = contentType.indexOf('text/event-stream') !== -1 || contentType.indexOf('stream') !== -1 || contentType.indexOf('text/plain') !== -1;

      if (!res.body || !isStream) {
        const data = await res.json().catch(() => ({}));
        const reply = (data.data && data.data.content) || data.content || data.message || '';
        this.chatMessages.push({ role: 'assistant', content: reply || '暂无回复' });
        streamDone();
        return;
      }

      this.chatMessages.push({ role: 'assistant', content: '', streaming: true });
      const streamMsg = this.chatMessages[this.chatMessages.length - 1];
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      const isPlainText = contentType.indexOf('text/plain') !== -1;

      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          if (isPlainText) {
            fullContent += chunk;
            this.$set(streamMsg, 'content', fullContent);
            this.$nextTick(() => this.scrollChatToBottom());
            continue;
          }

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.startsWith('data:')) {
              const raw = line.replace(/^data:\s*/, '').trim();
              if (raw === '[DONE]' || !raw) continue;
              try {
                const data = JSON.parse(raw);
                const delta = (data.choices && data.choices[0] && data.choices[0].delta && data.choices[0].delta.content) || data.content || data.delta || (typeof data.text === 'string' ? data.text : '');
                if (delta) {
                  fullContent += delta;
                  this.$set(streamMsg, 'content', fullContent);
                  this.$nextTick(() => this.scrollChatToBottom());
                }
              } catch (_) {}
            }
          }
        }
        if (isPlainText && buffer) fullContent += buffer;
        if (!fullContent.trim()) {
          const last = buffer.trim();
          if (last) {
            try {
              const data = JSON.parse(last);
              const c = (data.choices && data.choices[0] && data.choices[0].message && data.choices[0].message.content) || data.content || '';
              if (c) fullContent = c;
            } catch (_) {
              fullContent = last;
            }
          }
          this.$set(streamMsg, 'content', fullContent || '暂无回复');
        }
      } finally {
        streamDone();
      }
    },
    extractCodeToContent(assistantText) {
      if (!assistantText || typeof assistantText !== 'string') return;
      const htmlBlock = /```html\s*([\s\S]*?)```/i.exec(assistantText);
      const anyBlock = /```(?:html)?\s*([\s\S]*?)```/i.exec(assistantText);
      const code = (htmlBlock && htmlBlock[1]) || (anyBlock && anyBlock[1]) || '';
      const trimmed = code.trim();
      if (trimmed) {
        this.editorContent = trimmed;
        this.viewMode = 'code';
        this.$Message.success('已提取代码到编辑器');
      }
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        const wrap = this.$refs.chatMessagesWrap;
        if (wrap) wrap.scrollTop = wrap.scrollHeight;
      });
    },
    async saveContent() {
      if (!this.editorEditId) return;
      this.contentSaving = true;
      try {
        await this.$api.put(`${API_BASE}/${this.editorEditId}`, {
          content: this.editorContent,
          skills: this.getEditorSkillsIds(),
        });
        this.$Message.success('保存成功');
        const row = this.items.find(r => r.id === this.editorEditId);
        if (row) {
          row.content = this.editorContent;
          row.skills = this.getEditorSkillsIds();
        }
      } catch (err) {
        this.$Message.error(err.message || '保存失败');
      } finally {
        this.contentSaving = false;
      }
    },
    openMetaEdit(row) {
      this.metaModalTitle = '修改应用';
      this.metaEditId = row.id;
      this.metaFormData = {
        name: row.name || '',
        icon: row.icon || '',
        content: row.content || '',
        description: row.description || '',
        private: row.private !== false,
        disabled: row.disabled === 1 ? 1 : 0,
      };
      this.metaModalVisible = true;
    },
    async handleMetaSubmit() {
      try {
        await this.$refs.metaFormRef.validate();
        this.metaConfirmLoading = true;
        const payload = {
          name: this.metaFormData.name,
          icon: this.metaFormData.icon || undefined,
          description: this.metaFormData.description || undefined,
          private: this.metaFormData.private,
        };
        if (this.metaEditId) {
          payload.content = this.metaFormData.content;
          payload.disabled = this.metaFormData.disabled;
          await this.$api.put(`${API_BASE}/${this.metaEditId}`, payload);
          this.$Message.success('修改成功');
        } else {
          payload.content = this.metaFormData.content || '';
          await this.$api.post(API_BASE, payload);
          this.$Message.success('新建成功');
        }
        this.metaModalVisible = false;
        this.fetchData();
      } catch (err) {
        if (err && err.message) this.$Message.error(err.message);
      } finally {
        this.metaConfirmLoading = false;
      }
    },
    async toggleDisabled(row, value) {
      const disabled = value === 1 ? 1 : 0;
      try {
        await this.$api.put(`${API_BASE}/${row.id}`, { disabled });
        row.disabled = disabled;
        this.$Message.success(disabled ? '已禁用' : '已启用');
      } catch (err) {
        this.$Message.error(err.message || '操作失败');
      }
    },
    handleDelete(row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定要删除应用「${row.name}」吗？`,
        onOk: async () => {
          try {
            await this.$api.delete(`${API_BASE}/${row.id}`);
            this.$Message.success('删除成功');
            this.fetchData();
          } catch (err) {
            this.$Message.error(err.message || '删除失败');
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.miniapps-page.full-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
.search-bar .flex-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 15px;
  display: flex;
  flex-direction: column;
}
.table-wrap >>> .gz-table,
.table-wrap >>> .ivu-table-wrapper {
  height: 100%;
  flex: 1;
  min-height: 0;
}
.table-wrap >>> .ivu-table-body {
  overflow: auto;
}
.icon-clip-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.icon-clip-wrap .l-gz-image {
  width: 80px;
  height: 80px;
  min-width: 80px;
  min-height: 80px;
}
.icon-hint {
  font-size: 12px;
  color: var(--subtext2);
}
.form-hint {
  margin-left: 8px;
  font-size: 12px;
  color: var(--subtext2);
}

.miniapp-editor-layout {
  display: flex;
  height: 70vh;
  gap: 16px;
}
.editor-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.editor-left-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg2);
  flex-shrink: 0;
}
.preview-size-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--border);
  background: var(--bg2);
  flex-shrink: 0;
}
.preview-size-label {
  font-size: 12px;
  color: var(--subtext2);
  margin-right: 4px;
}
.editor-left-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.preview-viewport {
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  flex-shrink: 0;
  position: relative;
}
.preview-iframe-wrap {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}
.preview-iframe {
  display: block;
  border: none;
  background: #fff;
}
.preview-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subtext2);
  font-size: 13px;
}
.status-text {
  font-size: 12px;
  color: var(--subtext2);
}
.code-textarea {
  width: 100%;
  min-height: 400px;
  font-family: monospace;
  font-size: 13px;
}
.editor-right {
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.editor-right-top,
.editor-right-bottom {
  flex: 1;
  min-height: 120px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.editor-right-top.chat-panel {
  min-height: 200px;
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
}
.chat-panel-header {
  flex-shrink: 0;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.02);
}
.chat-panel-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--text, #333);
}
.panel-title {
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
  flex-shrink: 0;
}
.panel-placeholder {
  font-size: 12px;
  color: var(--subtext2);
}
.chat-messages-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}
.dev-chat-msg {
  margin-bottom: 14px;
}
.dev-chat-msg:last-child {
  margin-bottom: 0;
}
.dev-chat-msg.msg-user .msg-bubble {
  margin-left: 28px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary) 100%);
  color: #fff;
  border-radius: 14px 14px 4px 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.dev-chat-msg.msg-bot .msg-bubble {
  margin-right: 28px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px 14px 14px 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.msg-bubble {
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}
.msg-bubble.bot.typing {
  color: var(--subtext2);
  font-style: italic;
}
.msg-markdown-wrap {
  max-width: 100%;
}
.msg-markdown-wrap >>> .markdown-body {
  font-size: 13px;
  line-height: 1.55;
}
.cursor-blink::after {
  content: '|';
  animation: blink 0.8s step-end infinite;
}
@keyframes blink {
  50% { opacity: 0; }
}
.chat-input-row {
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding: 12px;
  border-top: 1px solid var(--border);
  background: rgba(0, 0, 0, 0.02);
}
.chat-input-row .ivu-input-wrapper,
.chat-input-row .chat-input-area {
  flex: 1;
  min-width: 0;
}
.chat-input-row .ivu-input-wrapper textarea {
  border-radius: 8px;
  resize: none;
}
.chat-send-btn {
  flex-shrink: 0;
}
.editor-right-bottom {
  position: relative;
  padding: 12px;
}
.skills-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.skill-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border);
}
.skill-item:last-child {
  border-bottom: none;
}
.skill-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.skill-name {
  font-size: 13px;
  font-weight: 500;
}
.skill-desc {
  font-size: 12px;
  color: var(--subtext2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
